import React, { useState, useEffect, useContext } from 'react'
import PageTitle from '../../components/PageTitle'
import ProductCard from '../../components/ProductCard'
import GeneralImageHero from '../../components/GeneralImageHero'
import Context from '../../context'
import CardSubCategories from '../../components/CardSubCategories'
import Head from 'next/head'
import FilterCount from '../../components/FilterCount'

import getConfig from 'next/config'
import BackToTop from '../../components/BackToTop'
import { useAuthAPI } from '../../hooks/api.hooks'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Keyboard, Pagination, FreeMode, Navigation } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import 'swiper/css/keyboard'

import { useIonModal } from '@ionic/react'
import Filters from '../../components/Filters'
import { enterAnimation, leaveAnimation } from '../../utils/modal-animations'
import ContentGroup from '../../components/ContentGroup'

const { publicRuntimeConfig } = getConfig()

const Collection = ({ items, slug }) => {
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string }>({})
  const [offset, setOffset] = useState(0)
  const [filters, setFilters] = useState({})
  const [sort, setSort] = useState<{ [key: string]: string }>({})
  const { state } = useContext(Context)
  const [products, setProducts] = useState([...items[0].template.skus])
  const [sortedProducts, setSortedProducts] = useState([...items[0].template.skus])
  const filtersSet = Object.keys(selectedFilters).length > 0

  const authAPICall = useAuthAPI()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.br_data = window.br_data || {}
      window.br_data.acct_id = publicRuntimeConfig.BR_ID
      window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
      window.br_data.ptype = 'collection'
      window.br_data.cat_id = slug
      window.br_data.cat = slug
      window.br_data.user_id = state.user.UID || state.deviceId
      window.br_data.view_id = state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE;

      (() => {
        const brtrk = document.createElement('script')
        brtrk.type = 'text/javascript'
        brtrk.async = true
        brtrk.src = `//cdn.brcdn.com/v1/br-trk-${publicRuntimeConfig.BR_ID}.js?v=${Date.now()}`
        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(brtrk, s)
      })()
    }
  }, [])

  useEffect(() => {
    const obj: { [key: string]: any } = {
      in_store_only: []
    }
    items[0].template.skus.forEach((item) => {
      const filters = item.filter_attr
      for (const key in filters) {
        if (!obj[key]) {
          obj[key] = filters[key]
        } else {
          obj[key] = [...new Set([].concat(...obj[key], ...filters[key]))]
        }
      }
      obj.in_store_only.push(`${item.in_store_only}`)
    })
    if (obj.Availability) {
      delete obj.Availability
    }
    setFilters(obj)
  }, [])

  useEffect(() => {
    let arr = []
    if (Object.keys(selectedFilters)?.some(key => selectedFilters[key].length)) {
      if (Object.keys(selectedFilters).length === 1 && selectedFilters.in_store_only) {
        arr = products
      } else {
        products.forEach((item) => {
          const filters = item.filter_attr
          if (filters) {
            for (const key in selectedFilters) {
              if (key !== 'in_store_only' && filters[key]) {
                const intersection = filters[key].filter((value) => selectedFilters[key].includes(value))
                if (intersection.length && !arr.some((e) => e.slug === item.slug)) {
                  arr.push(item)
                }
              }
              if (key === 'on_hand_qty' && item.inventory > 0) {
                arr.push(item)
              }
            }
          }
        })
      }
    } else {
      arr = products
    }
    if (selectedFilters.in_store_only?.length && selectedFilters.in_store_only?.length === 1) {
      arr = selectedFilters.in_store_only.includes('true') ? arr.filter((item) => item.in_store_only) : arr.filter((item) => !item.in_store_only)
    }
    if (sort.price === 'asc') {
      arr.sort((a, b) => a.final_price - b.final_price)
    } else if (sort.price === 'desc') {
      arr.sort((a, b) => b.final_price - a.final_price)
    } else if (sort.avg_rating === 'asc') {
      arr.sort((a, b) => a.avg_rating - b.avg_rating)
    } else if (sort.avg_rating === 'desc') {
      arr.sort((a, b) => b.avg_rating - a.avg_rating)
    }
    setSortedProducts([...arr])
  }, [selectedFilters, sort, products])

  useEffect(() => {
    setSelectedFilters({})
    authAPICall(
      'productinfo',
      'product-group',
      'GET',
      {
        slug,
        popkey: publicRuntimeConfig.POP_KEY,
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
      },
      {}
    ).then((data) => setProducts([...data[0].template.skus]))
  }, [state.cart.store?.storenumber])

  useEffect(() => {
    if (offset !== 0) {
      const stringifyFirstProduct = String(sortedProducts[offset].slug)
      const firstProduct = document.getElementById(stringifyFirstProduct)
      firstProduct.querySelector('a').focus()
      firstProduct.scrollIntoView({ behavior: 'smooth' })
    }
  }, [offset])

  const [grid, setOpen] = React.useState(false)

  const renderContent = () => {
    const offsetArray = sortedProducts.slice(0, offset + 24)
    const arr = offsetArray.map((item, i) => {
      return (
        <div id={item.slug} key={item.slug}>
          <ProductCard
            key={`${item.slug}-${i}`}
            data={{ ...item, in_store_only: `${item.in_store_only}` }}
            vertical={!grid}
          />
        </div>
      )
    })
    return arr
  }

  const renderCarousel = () => {
    const swiperConfig = {
      modules: [FreeMode, Pagination, Navigation, Keyboard],
      freeMode: true,
      pagination: { clickable: true },
      navigation: {
        nextEl: '.swiper-button-next-collection',
        prevEl: '.swiper-button-prev-collection'
      },
      spaceBetween: 16,
      keyboard: true,
      watchOverflow: true
    }

    const displayedSubs = items[0].template.children

    const arr = displayedSubs.map((category) => (
      <SwiperSlide key={category.link}>
        <CardSubCategories
          title={category.title}
          slug={category.link}
          image={category.image_url}
          campaignName={category.campaign_name}
          dateReference={category.date_reference}
        />
      </SwiperSlide>
    ))

    return (
      <section className='swiper__section content-carousel'>
        <div className="swiper-button-prev-collection swiper-navbutton swiper-navbutton-prev"></div>
        <div className="swiper-button-next-collection swiper-navbutton swiper-navbutton-next"></div>
        <Swiper {...swiperConfig} slidesPerView={'auto'}>
          {arr}
        </Swiper>
      </section>
    )
  }

  const submitForm = (filters, sort) => {
    setSelectedFilters({ ...filters })
    window.br_data = window.br_data || {}
    window.br_data = window.br_data || {}
    window.br_data.acct_id = publicRuntimeConfig.BR_ID
    window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
    window.br_data.ptype = 'collection'
    window.br_data.view_id = state.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
    window.br_data.user_id = state.user.UID || state.deviceId

    window.br_data.orig_ref_url = location.href

    try {
      window.BrTrk.getTracker().updateBrData(window.br_data)
      window.BrTrk.getTracker().logPageView()
    } catch (e) {
      console.log(e)
    }
    setSort(sort)
    setOffset(0)
  }

  const handleDismiss = () => dismiss()
  const [present, dismiss] = useIonModal(Filters, { handleDismiss, data: filters, incomingData: { ...selectedFilters }, incomingSort: sort, submitForm, amt: sortedProducts.length, loading: false }
  )
  const toggleFilters = async () => {
    present({ cssClass: 'modal--filters', enterAnimation, leaveAnimation })
  }

  return (
    <div id="main-content" className="collection">
      <Head>
        <title>{items[0].title}</title>
        <meta name="description" content={'Collection at pOpshelf'} />
      </Head>
      <div className="collection__container">
        <div className="collection__header">
          <GeneralImageHero image={items[0].image_url} mobileImage={items[0].mobile_image} />
          <PageTitle title={items[0].title} />
        </div>
        {!!items[0].template.children.length && (
          <div className="collection__subcategories">
            <h2>sub categories</h2>
            <div className="collection__subcategories-carousel">
              {renderCarousel()}
            </div>
          </div>
        )}

        <div id="target-content-top">
          <ContentGroup position="top" />
        </div>
        <div
          style={{ overflowAnchor: 'none' }}
        >
          <div className="collection__head">
            {(!!products.length || !!Object.keys(selectedFilters).length) && (
              <div className="collection__selectors">
                <FilterCount results={sortedProducts.length} />
              </div>
            )}

            <div className={`collection__icons ${grid ? 'collection__icons--horizontal' : 'collection__icons--vertical'}`}>
              <button className="collection__grid-icon" onClick={() => setOpen(!grid)}>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </button>
              <button className="collection__list-icon" onClick={() => setOpen(!grid)}>
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
            <div className='collection__filters-button'>
              {(!!products.length || !!Object.keys(selectedFilters).length) && (
                <div className='filters__button-container'>
                  <button className={`filters__button ${filtersSet ? 'filters__button-bg' : ''}`} onClick={toggleFilters}>
                    <p>{filtersSet ? 'Filtered' : 'Filters'}</p>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className={`product-list-page ${grid ? 'product-list-page__grid' : ''}`}>{renderContent()}</div>
        </div>
        {!!sortedProducts.length && (
          <div className='collection__view-more'>
            {(sortedProducts.length > (offset + 24)) && (
              <button
                onClick={() => setOffset(offset + 24)}
                className="btn__secondary btn__secondary--full"
              >
                VIEW MORE
              </button>
            )}
          </div>
        )}
        <div id="target-content-bottom">
          <ContentGroup position="bottom" />
        </div>
        <BackToTop />
      </div>
    </div>
  )
}

export default Collection

export const getServerSideProps = async ({ req, query }) => {
  const { publicRuntimeConfig } = getConfig()
  const res = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/product-group?slug=${query.slug}&popkey=${publicRuntimeConfig.POP_KEY}&store=${publicRuntimeConfig.DEFAULT_STORE}`
  )
  const items = await res.json()

  return {
    props: {
      items,
      slug: query.slug
    }
  }
}
